import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import moment from "moment";
import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { EncodedPolyLine, FormGroup, MarkerProps } from "@cofano/cck";

import { Alert, Box, CircularProgress, Tab, Tabs, Typography, useTheme } from "@mui/material";

import { Language, usePermission, hasPluginPermission } from "imports/Utils";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import FleetSelect, { FleetSelectOption } from "view/fleets/components/selects/FleetSelect";
import CallSideviewer from "view/calls/components/sideviewer/CallSideviewer";
import { calculateCallStatus } from "view/calls/utils/CallUtils";

import { ChildPropsFromArray } from "types/commons";

import { CallTimeSource, MapShipTab, getVoyageRoute } from "../../utils/MapUtils";
import MapShipInformation from "../information/MapShipInformation";
import MapShipHistory from "../history/MapShipHistory";
import ShipMarker from "../markers/ShipMarker";

import { GetControlTowerCallsByShipDocument } from "__generated__/view/controltower/graphql/controlTower.queries.generated";
import { GetActualPositionsDocument, GetActualPositionsQuery, GetBatchRoutesDocument } from "__generated__/view/maps/graphql/map.queries.generated";
import { GetShipOnMapDocument } from "__generated__/view/ships/graphql/ship.queries.generated";

export type ActualPosition = ChildPropsFromArray<GetActualPositionsQuery, "actualPositions">

interface MapShipsTab {
	callTimeSource: CallTimeSource;
	onChange: (markers: MarkerProps[], encodedPolylines: EncodedPolyLine[], fitBounds?: boolean) => void;
}

const MapShipsTab = ({ callTimeSource, onChange }: MapShipsTab) => {

	const session = useStoreState(store => store.session);
	const user = useStoreState(state => state.session.user);

	const client = useApolloClient();
	const theme = useTheme();

	const [ searchParams, setSearchParams ] = useSearchParams();

	const { isAdmin, isCustomer, isOperator, isSkipperOrShipManager, isCompanyUser, isMainUserLimited } = usePermission();

	const fleetsEnabled = hasPluginPermission(session, "fleets");
	const hasFleetPermission = fleetsEnabled && (!(isCustomer || isOperator || isSkipperOrShipManager) || isAdmin);

	const { data } = useQuery(GetShipOnMapDocument, {
		variables: { id: user?.properties.shipIds?.[0] ?? -1 },
		skip: !isSkipperOrShipManager || !user?.properties.shipIds?.[0],
	});

	const getDefaultShip = () => {
		const param = searchParams.get("ship");
		return param ? JSON.parse(param) : undefined;
	};

	const getDefaultFleets = () => {
		const param = searchParams.get("fleets");
		if (param) {
			return JSON.parse(param);
		} else if (user?.properties.fleets) {
			return user.properties.fleets.map(it => ({ id: it.id, description: it.description }));
		} else {
			return null;
		}
	};

	const [ selectedShip, setSelectedShip ] = useState<ShipSelectOption | undefined>(getDefaultShip());
	const [ selectedFleets, setSelectedFleets ] = useState<FleetSelectOption[] | null>(hasFleetPermission ? getDefaultFleets() : null);
	const [ selectedTab, setSelectedTab ] = useState<MapShipTab>(MapShipTab.Information);
	const [ positions, setPositions ] = useState<ActualPosition[] | undefined | null>(undefined);
	const [ selectedPosition, setSelectedPosition ] = useState<ActualPosition | undefined>(undefined);

	const fitBoundsRef = useRef(true);

	const [ fetchBatchRoutes ] = useLazyQuery(GetBatchRoutesDocument);

	const [ getActualPositions, { loading } ] = useLazyQuery(GetActualPositionsDocument, {
		pollInterval: 60000,
		variables: {
			search: {
				shipIds: selectedShip ? [ selectedShip.id ] : undefined,
				fleetIds: !selectedShip && hasPluginPermission(session, "fleets") ? selectedFleets?.map(f => f.id) : null,
				excludeShipsWithChildCallAsNextCall: true,
			},
		},
		onCompleted: (data) => {
			showPositions(fitBoundsRef.current, data?.actualPositions);
			setPositions(data?.actualPositions);
			fitBoundsRef.current = false;
		},
	});

	const createMarker = (position: ActualPosition, index: number): MarkerProps | null => {
		if (position.latitude == null || position.longitude == null) {
			return null;
		}

		const difference = moment().diff(moment(position.timestamp), "minutes");
		const actual = difference < 30;

		const color = callTimeSource === CallTimeSource.Calculated ?
			(position.nextCall ? calculateCallStatus(position.nextCall, session).color : undefined) :
			(position.nextCall?.lastStatusChange?.status?.color ?? undefined);

		return {
			lat: position.latitude!,
			lng: position.longitude!,
			label: position.ship?.name,
			component: () => (
				<ShipMarker
					key={index}
					course={position.course}
					actual={actual}
					color={color}
					onClick={() => {
						if (selectedShip?.id === position.ship?.id) {
							setSelectedShip(undefined);
							setSelectedPosition(undefined);
						} else {
							setSelectedShip(position.ship === null ? undefined : position.ship);
							setSelectedPosition(position);
						}
					}}
					label={position.ship?.name} />
			),
		};
	};

	const showPositions = (bounds: boolean, pos: ActualPosition[] | undefined | null) => {
		if (selectedShip) {
			pos?.forEach((position: ActualPosition) => {
				if (position.latitude && position.longitude && position.ship?.id === selectedShip.id) {
					const polylines: EncodedPolyLine[] = [];
					const voyage = position.nextCall?.voyage;

					setSelectedPosition(position);

					const markers: MarkerProps[] = pos
						.map((position, index) => createMarker(position, index))
						.filter((marker): marker is MarkerProps => marker !== null);

					onChange(markers, [], bounds);

					if (voyage && selectedTab === MapShipTab.Calls) {
						getVoyageRoute(voyage, position, fetchBatchRoutes, client).then(route => {
							route.markers.forEach(m => {
								markers.push(m);
							});

							route.polylines.forEach(p => {
								polylines.push(p);
							});

							onChange(markers, polylines, bounds);

						});
					} else {
						onChange(markers, [], bounds);
					}
				}
			});
		} else {
			const positionMarkers: MarkerProps[] = [];

			pos?.forEach((position, index: number) => {
				if (position.latitude != null && position.longitude != null) {
					const marker = createMarker(position, index);
					if (marker) {
						positionMarkers.push(marker);
					}
				}
			});

			onChange(positionMarkers, [], bounds);
		}
	};

	useEffect(() => {
		fitBoundsRef.current = true;
		getActualPositions();

		setSearchParams((prev) => {
			const latestParams = new URLSearchParams(prev);

			if (selectedFleets && selectedFleets.length > 0 && hasFleetPermission) {
				latestParams.set("fleets", JSON.stringify(selectedFleets));
			} else {
				latestParams.delete("fleets");
			}

			if (selectedShip) {
				latestParams.set("ship", JSON.stringify(selectedShip));
			} else {
				latestParams.delete("ship");
			}

			return latestParams;
		}, {
			replace: true,
		});
	}, [ selectedShip, selectedFleets, selectedTab ]);

	useEffect(() => {
		if (isSkipperOrShipManager && data?.ship !== undefined) {
			setSelectedShip(data.ship);
		}
	}, [ data?.ship?.id ]);

	return (
		<Box p={2}>
			{hasFleetPermission && (
				<FormGroup label={intl.formatMessage({ id: "SELECT_FLEET" })}>
					<FleetSelect
						value={selectedFleets}
						multiple={true}
						disabled={loading}
						onChange={(op) => setSelectedFleets(op)} />
				</FormGroup>
			)}

			<FormGroup label={intl.formatMessage({ id: "SHIP" })}>
				<ShipSelect
					value={selectedShip}
					disabled={loading}
					onChange={(op) => {
						if (op) {
							setSelectedShip(op);
						} else {
							setSelectedShip(undefined);
							onChange([], []);
						}
					}}
					additionalSearchOptions={{
						fleets: selectedFleets && selectedFleets.length > 0 ? selectedFleets.map(fleet => fleet.id) : undefined,
					}} />
			</FormGroup>

			<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
				<Tabs
					value={selectedTab}
					onChange={(event, newValue) => setSelectedTab(newValue)}>
					<Tab label={Language("DASHBOARD_SHIP_INFORMATION")} value={MapShipTab.Information} />

					{(isAdmin || isCompanyUser || isOperator || isMainUserLimited) && (
						<Tab label={Language("HISTORY")} value={MapShipTab.History} />
					)}

					<Tab label={Language("CALLS")} value={MapShipTab.Calls} />
				</Tabs>
			</Box>

			{selectedTab === MapShipTab.Information && (
				<React.Fragment>
					{selectedShip ? (
						<MapShipInformation
							shipId={selectedShip.id}
							position={positions?.find(p => p.ship?.id === selectedShip.id)} />
					) : (
						<Typography variant="h4" sx={{ fontSize: 14 }} color="textPrimary">
							{intl.formatMessage({ id: "SELECT_SHIP" })}
						</Typography>
					)}
				</React.Fragment>
			)}

			{selectedTab === MapShipTab.History && (
				<React.Fragment>
					{selectedShip ? (
						<MapShipHistory ship={selectedShip} onChange={onChange} />
					) : (
						<Typography variant="h4" sx={{ fontSize: 14 }} color="textPrimary">
							{intl.formatMessage({ id: "SELECT_SHIP" })}
						</Typography>
					)}
				</React.Fragment>
			)}

			{(selectedTab === MapShipTab.Calls) && (
				<Box sx={{ backgroundColor: theme.palette.mode === "dark" ? "#15171C" : "#F4F5F7", margin: -2, p: 2 }}>
					{(selectedShip && selectedPosition?.nextCall) ? (
						<CallSide
							key={selectedShip.id}
							selectedPosition={selectedPosition}
							ship={selectedShip} />
					) : (
						<React.Fragment>
							{selectedShip && !selectedPosition?.nextCall ? (
								<Alert severity="warning">
									{Language("NO_CALLS_FOUND")}
								</Alert>
							) : (
								<Typography variant="h4" sx={{ fontSize: 14 }} color="textPrimary">
									{intl.formatMessage({ id: "SELECT_SHIP" })}
								</Typography>
							)}
						</React.Fragment>
					)}
				</Box>
			)}
		</Box>
	);
};

const CallSide = ({ selectedPosition, ship }: { selectedPosition: ActualPosition, ship: ShipSelectOption }) => {

	const [ fetchCalls, { loading, error, data } ] = useLazyQuery(GetControlTowerCallsByShipDocument, {
		fetchPolicy: "network-only",
	});

	const calls = data?.callsByShip ?? [];

	useEffect(() => {
		if (selectedPosition.nextCall?.id) {
			fetchCalls({
				variables: {
					shipId: ship.id,
				},
			});
		}
	}, [ selectedPosition.nextCall?.id, ship.id, fetchCalls ]);

	if (loading) {
		return <CircularProgress size={24} />;
	}

	if (error) {
		return <Alert severity="error">	{intl.formatMessage({ id: "CONTROL_TOWER_CALLS_LOADING_ERROR" })}</Alert>;
	}

	return (
		<CallSideviewer
			defaultCallId={calls?.[0]?.id ?? null}
			nextCallId={selectedPosition.nextCall?.id ?? null}
			refetch={() => fetchCalls({ variables: { shipId: ship.id } })}
			bargeType={ship?.bargeType ?? null}
			isShipEditable={false}
		/>
	);
};

export default MapShipsTab;
